import { useCallback, useEffect, useRef, useState } from "react";

/** A 2-D point in natural canvas coordinates */
export type Point = { x: number; y: number };

/**
 * Encapsulates the full masking / canvas-drawing workflow used by image-edit flow.
 *  – Manages stroke history & redo stack
 *  – Provides pointer handlers for drawing
 *  – Exposes `getMaskBlob` that returns an RGBA PNG where painted pixels are transparent
 *
 * Usage:
 * const mask = useMasking();
 */
export const useMasking = () => {
  /* ─────────────── State ─────────────── */
  const [isMaskingEnabled, setIsMaskingEnabled] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [strokes, setStrokes] = useState<Point[][]>([]);
  const [redoStack, setRedoStack] = useState<Point[][]>([]);
  const drawingRef = useRef(false);
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);

  /** Convert a pointer-event position to *natural* canvas coordinates */
  const getCoords = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  /** Redraw every stroke – used for undo / redo */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw all strokes in one pass to prevent overlapping
    ctx.globalCompositeOperation = "copy";
    ctx.strokeStyle = "#21D9FE66";
    ctx.lineWidth = 140; //To Match 48px cursor size
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Draw all strokes in a single path
    ctx.beginPath();
    strokes.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.moveTo(stroke[0]?.x, stroke[0]?.y);
      stroke.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    });
    ctx.stroke();
  }, [strokes]);

  useEffect(redraw, [redraw]);

  /* ─────────── Pointer events ─────────── */
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isMaskingEnabled) return;
    drawingRef.current = true;
    setRedoStack([]); // new stroke invalidates redo history
    const start = getCoords(e);
    setStrokes((prev) => [...prev, [start]]);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isMaskingEnabled || !drawingRef.current) return;
    const point = getCoords(e);
    setStrokes((prev) => {
      const next = [...prev];
      next[next.length - 1] = [...next[next.length - 1], point];
      return next;
    });
  };

  const finishStroke = () => {
    drawingRef.current = false;
  };

  /* ───────────── Undo / Redo ───────────── */
  const handleUndo = () => {
    setStrokes((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const popped = next.pop()!;
      setRedoStack((r) => [...r, popped]);
      return next;
    });
  };

  const handleRedo = () => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const nextRedo = [...prev];
      const stroke = nextRedo.pop()!;
      setStrokes((s) => [...s, stroke]);
      return nextRedo;
    });
  };

  const lineWidthPx = 140; // To match 48px cursor size

  /**
   * Draws all user strokes onto the supplied 2D context using an *opaque* color.
   * This is used while building the export mask to guarantee the alpha value is 1
   * so that the punched-out area becomes *fully* transparent (no leftover tint).
   */
  const drawStrokesOpaque = (ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = "#ffffff"; // fully-opaque color – actual hue does not matter
    ctx.lineWidth = lineWidthPx;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();
    strokes.forEach((stroke) => {
      if (stroke.length === 0) return;
      if (stroke.length === 1) {
        // single-dot stroke – draw a minuscule line to render a dot
        const p = stroke[0];
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + 0.1, p.y + 0.1);
      } else {
        ctx.moveTo(stroke[0]?.x, stroke[0]?.y);
        stroke.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      }
    });
    ctx.stroke();
  };

  /* ─────────── Mask export ─────────── */
  const getMaskBlob = async (): Promise<Blob | null> => {
    const strokeCanvas = canvasRef.current;
    if (!strokeCanvas) return null;
    if (strokes.length === 0) return null;

    const { width: w, height: h } = strokeCanvas;

    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = w;
    maskCanvas.height = h;
    const ctx = maskCanvas.getContext("2d");
    if (!ctx) return null;

    // Opaque background (white/black – color irrelevant, alpha 1)
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, w, h);

    // Transparent holes where painted – we need *fully* transparent pixels
    ctx.globalCompositeOperation = "destination-out";
    drawStrokesOpaque(ctx);
    ctx.globalCompositeOperation = "source-over";

    return new Promise((resolve) => {
      maskCanvas.toBlob((b) => resolve(b ?? null), "image/png", 1);
    });
  };

  /* Create an image that keeps the original pixels and punches out
     transparent holes wherever the user painted */
  const getMaskedImageBlob = async (): Promise<Blob | null> => {
    const strokeCanvas = canvasRef.current;
    const img = imgRef.current;
    if (!strokeCanvas || !img) return null;
    if (strokes.length === 0) return null;

    const { width: w, height: h } = strokeCanvas;

    const previewCanvas = document.createElement("canvas");
    previewCanvas.width = w;
    previewCanvas.height = h;
    const ctx = previewCanvas.getContext("2d");
    if (!ctx) return null;

    // Draw the original image first
    ctx.drawImage(img, 0, 0, w, h);

    // Punch transparent holes where the user painted
    ctx.globalCompositeOperation = "destination-out";
    drawStrokesOpaque(ctx);
    ctx.globalCompositeOperation = "source-over";

    return new Promise((resolve) => {
      previewCanvas.toBlob((b) => resolve(b ?? null), "image/png", 1);
    });
  };

  /* ─────────── Public API ─────────── */
  return {
    // state
    isMaskingEnabled,
    setIsMaskingEnabled,
    strokes,
    redoStack,
    imgSize,
    setImgSize,
    // refs
    canvasRef,
    imgRef,
    // handlers
    handlePointerDown,
    handlePointerMove,
    finishStroke,
    handleUndo,
    handleRedo,
    // utils
    getMaskBlob,
    getMaskedImageBlob,
  } as const;
};
